/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGlobalSearch, useProcess } from "@/global-data";
import { useDeletePopup } from "@/global-data/use-delete-popup";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useToast } from "@/hooks/use-toast";
import { getUserInfo } from "@/lib/token";
import { getJobId } from "@/lib/utils";
import { wait } from "@/lib/wait";
import { apiServices } from "@/services/api.services";
import {
  companies,
  // jobId,
  retailers,
  roles,
  users,
} from "@/services/global.services";
import { useEffect, useMemo, useState } from "react";

export const useMutations = () => {
  const API_SERVICE = users;
  const { setProcess } = useProcess();
  const [skeltonLoading, setSkeltonLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [currentUserData, setCurrentUserData] = useState<any>(undefined);
  const [companyData, setCompanyData] = useState([]);
  const [formPopupOpen, setFormPopupOpen] = useState(false);
  const [activeRecordData, setActiveRecordData] = useState<any>(undefined);
  const [tableColumn, setTableColumn] = useState([]);
  const [filter, setFilter] = useState({});
  const { company_id, user_id } = useMemo(() => {
    const tempUserData = getUserInfo();
    return {
      company_id: tempUserData?.company_id,
      user_id: tempUserData?.user_id || "",
    };
  }, []);

  const [roleList, setRoleList] = useState([]);

  const [deleteId, setDeleteId] = useState(undefined);

  const { setSearchProgress, search } = useGlobalSearch();
  const { toast } = useToast();

  const [searchDebounced] = useDebouncedValue(search, 200);

  const { onDelete, status, identity, clearDelete } = useDeletePopup();

  const [paginationData, setPaginationData] = useState({
    page: 1,
    perPage: 10,
    totalItem: 1,
  });
  const [errorMessage, setErrorMessage] = useState<any>({
    title: "",
    message: "",
    open: false,
  });

  const closeErrorMessage = () => {
    setErrorMessage({
      title: "",
      message: "",
      open: false,
    });
  };

  //On Load Component
  useEffect(() => {
    (async () => {
      await loadingCurrentUsers();
    })();
    return () => {
      closeErrorMessage();
    };
  }, []);
  //On Load Component
  useEffect(() => {
    (async () => {
      if (currentUserData !== undefined) {
        await loadingUsers({ searchText: "" });
        await loadingCompany();
        await loadRoleList();
        await wait(1000);
        setSkeltonLoading(false);
        setProcess(false);
      }
    })();
  }, [currentUserData]);

  const loadingCurrentUsers = async () => {
    const payload = {
      data: {
        filter_by: {
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message /* error, message, statusCode */ } =
      await apiServices<any>(API_SERVICE.get, "post", payload);

    if (data) {
      const userData = data.result.metadata[API_SERVICE.key][0];
      setCurrentUserData(userData);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
      setSkeltonLoading(false);
    }
    return { data, error };
  };

  //handle delete
  useEffect(() => {
    if (status === "cancel") {
      setDeleteId(undefined);
      clearDelete();
    }
    if (status === "delete" && identity === "user") {
      (async () => {
        setProcess(true);
        const deleteResponse = await deleteRecord();
        if (deleteResponse.data) {
          if (deleteResponse.data?.job_id) {
            const jobIdResponse = await getJobId(deleteResponse.data?.job_id);
            if (jobIdResponse.data) {
              // return jobIdResponse;
              await loadingUsers({});
              toast({
                title: "Success",
                variant: "success",
                description: jobIdResponse.data.result.metadata.message,
              });
            }
          }
        }
        clearDelete();
        setDeleteId(undefined);
        setProcess(false);
      })();
    }
  }, [status]);

  const deleteRecord = async () => {
    const payload = {
      data: {
        user_id: deleteId,
        company_id: company_id,
      },
      company_id: company_id,
    };
    const { data, error, message } = await apiServices<any>(
      API_SERVICE.delete,
      "post",
      payload
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }

    return { data, error };
  };

  //Handle Search
  useEffect(() => {
    if (skeltonLoading === false) {
      (async () => {
        setSearchProgress(true);
        /* const response =  */
        await loadingUsers({});
        setSearchProgress(false);
      })();
    }
  }, [searchDebounced]);

  const onFormOpenChange = (prop: boolean) => {
    setFormPopupOpen(prop);
  };

  const onPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, page: prop }));
    setProcess(true);
    await loadingUsers({ page: prop });
    setProcess(false);
  };
  const onPerPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, perPage: prop, page: 1 }));
    setProcess(true);
    await loadingUsers({ perPage: prop, page: 1 });
    setProcess(false);
  };

  const permission: any = [];
  const onAction = async ({ action, data }: any) => {
    if (action === "edit") {
      setActiveRecordData(data);
      onFormOpenChange(true);
    }
    if (action === "delete") {
      setActiveRecordData(data);
      setDeleteId(data.user_id);
      onDelete({
        description:
          "This action cannot be undone. This will permanently delete the record and remove its data from our system.",
        id: data.user_id,
        identity: "user",
        popupStatus: true,
        title: "Are you sure you want to delete this user?",
      });
      await loadingUsers({});
    }
  };

  useEffect(() => {
    if (formPopupOpen === false) {
      setActiveRecordData(undefined);
      closeErrorMessage();
    }
  }, [formPopupOpen]);

  const loadingUsers = async ({
    perPage,
    page,
    filterReset,
    makeFilter,
    searchText,
  }: {
    perPage?: number;
    page?: number;
    filterReset?: boolean;
    makeFilter?: boolean;
    searchText?: string;
  }) => {
    // const userData: any = getUserInfo();

    let apiFilter = {};

    if (!filterReset) {
      apiFilter = filter;
    }

    let page_no = page || paginationData.page;

    if (makeFilter) {
      page_no = 1;
    }

    const payload = {
      data: {
        // username: userData?.username,
        filter_by: {
          ...apiFilter,
          search_text: searchText ?? searchDebounced,
          exact_match: false,
          pagination: {
            page_no: page_no,
            no_of_recs: perPage || paginationData.perPage,
            sort_by: "cts",
            order_by: false,
          },
          //"delivery_type": this.delivery_type,
        },
      },

      company_id: company_id === "system" ? "" : currentUserData.company_id,
      user_id: company_id === "system" ? "" : currentUserData.user_id,
    };

    console.log("page_no", payload);

    const { data, error, message } = await apiServices<any>(
      API_SERVICE.get,
      "post",
      payload
    );

    if (data) {
      setListData(data.result.metadata.users);
      setPaginationData((prev) => ({
        ...prev,
        totalItem: data?.result?.metadata?.total_records || 1,
      }));
    }

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }

    return { data, error };
  };

  const loadingCompany = async () => {
    // const userData: any = getUserInfo();
    const payload = {
      data: {
        filter_by: {
          // company_id: [currentUserData.company_id],
          search_text: "",
          exact_match: false,
        },
      },
      company_id: currentUserData.company_id,
      user_id: company_id === "system" ? "" : currentUserData.user_id,
    };
    const { data, error, message /* error, message, statusCode */ } =
      await apiServices<any>(companies.get, "post", payload);

    if (data) {
      const companyList = data.result.metadata.company.map((item: any) => ({
        company_id: item.company_id,
        company_name: item.display_name,
      }));

      setCompanyData(companyList);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }

    return { data, error };
  };

  // const getJobId = async (prop: string) => {
  //   const { data, error, message } = await apiServices<any>(
  //     jobId.get + prop,
  //     "get"
  //   );

  //   if (data?.process_status === "in-progress") {
  //     const jobResponse: any = await getJobId(prop);
  //     return jobResponse;
  //   }
  //   if (data?.status?.error_details?.fail_message) {
  //     // toast({
  //     //   title: "Error",
  //     //   variant: "destructive",
  //     //   description: data.status.error_details.fail_message,
  //     // });
  //     setErrorMessage({
  //       title: "Error",
  //       message: data.status.error_details.fail_message,
  //       open: true,
  //     });
  //     return { data: null, error: true };
  //   }
  //   if (error) {
  //     setErrorMessage({
  //       title: "Error",
  //       message: error.status?.message || message,
  //       open: true,
  //     });
  //     // toast({
  //     //   title: "Error",
  //     //   variant: "destructive",
  //     //   description: error.status?.message || message,
  //     // });
  //   }

  //   return { data, error };
  // };

  const onFormSubmit = async (prop: any): Promise<any> => {
    const currentCompany: any = companyData.find(
      (item: any) => item.company_id === company_id
    );

    let formData = {};
    if (activeRecordData?.user_id) {
      formData = {
        role_id: prop.role_id,
        username: prop.username,
        password: prop.password,
        company_id:
          company_id === "system" ? prop.company_id : currentCompany.company_id,
        company_name:
          company_id === "system"
            ? prop.company_name
            : currentCompany.company_name,
        email: prop.email,
        linked_customer: prop.linked_customer,
        user_id: activeRecordData.user_id,
      };
    } else {
      formData = {
        username: prop.username,
        password: prop.password,
        company_id:
          company_id === "system" ? prop.company_id : currentCompany.company_id,
        company_name:
          company_id === "system"
            ? prop.company_name
            : currentCompany.company_name,
        email: prop.email,
        linked_customer: prop.linked_customer,
        role_id: prop.role_id,
      };
    }
    const payload = {
      data: formData,
      company_id: prop.company_id,
      user_id: company_id === "system" ? "" : currentUserData.user_id,
    };

    const { data, error } = await apiServices<any>(
      API_SERVICE.post,
      "post",
      payload
    );

    if (data?.job_id) {
      const jobIdResponse = await getJobId(data.job_id);
      if (jobIdResponse.error) {
        return jobIdResponse;
      }
      const getResponse = await loadingUsers({});
      if (getResponse.error) {
        return getResponse;
      }
      if (getResponse.data) {
        toast({
          title: "Success",
          variant: "success",
          description:
            jobIdResponse?.data?.result?.metadata?.message ??
            jobIdResponse?.data?.status?.msg ??
            jobIdResponse?.data?.status?.message,
        });
      }
    }

    if (error) {
      setProcess(false);
    }
    return { data, error };
  };

  const filterRequest = async () => {
    setProcess(true);
    await loadingUsers({ makeFilter: true });
    setProcess(false);
  };
  const clearFilter = async () => {
    setFilter({});
    setProcess(true);
    await loadingUsers({ filterReset: true });
    setProcess(false);
  };

  const loadRoleList = async () => {
    const payload = {
      data: {
        filter_by: {
          company_id: [company_id],
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      roles.get,
      "post",
      payload
    );

    if (data) {
      console.log(data.result.metadata);
      setRoleList(data.result.metadata[roles.key]);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };
  const loadFormRoleList = async (user_company_id: string) => {
    const payload = {
      data: {
        filter_by: {
          company_id: [user_company_id],
          search_text: "",
          exact_match: false,
        },
      },
      company_id: user_company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      roles.get,
      "post",
      payload
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data: data?.result?.metadata[roles.key], error };
  };

  const loadFormRetailerList = async (user_company_id: string) => {
    const payload = {
      data: {
        filter_by: {
          company_id: [user_company_id],
          search_text: "",
          exact_match: false,
        },
      },
      company_id: user_company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      retailers.get,
      "post",
      payload
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data: data?.result?.metadata[retailers.key], error };
  };

  return {
    skeltonLoading,
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    tableColumn,
    setTableColumn,
    companyData,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    company_id,
    errorMessage,
    closeErrorMessage,
    roleList,
    loadFormRoleList,
    loadFormRetailerList,
  };
};
